import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Drawer,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { publicApi } from "../../api/axiosConfig";

import ProductSortbar from "./Utils/ProductSortBar";
import ProductImage from "./Utils/ProductImage";
import FilterSideBar from "./Utils/FilterSideBar";
import NotFoundPage from "../404/NotFoundPage";

import BreadcrumbsBar from "../Product/Utils/BreadcrumbsBar";

export default function CategoryProducts() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showDesktopFilters, setShowDesktopFilters] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setActiveFilters({});
    setNotFound(false);
  }, [slug]);

  useEffect(() => {
    const fetchCategoryProducts = async () => {
      setLoading(true);
      setNotFound(false);
      setError("");

      try {
        const cleanFilters = {};

        Object.entries(activeFilters).forEach(([key, value]) => {
          if (
            value !== "" &&
            value !== null &&
            value !== undefined &&
            value !== false
          ) {
            cleanFilters[key] = value;
          }
        });

        const params = {
          category: slug,
          ...cleanFilters,
        };

        let data;

        const res = await publicApi.get("/api/v1/products/", {
          params,
        });

        data = res.data;

        setProducts(data.results || []);

        if (data.meta) {
          setMeta(data.meta);
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError(404);
        } else {
          console.error(
            "Failed to fetch category products",
            err,
          );
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchCategoryProducts();
    }
  }, [slug, activeFilters]);

  // Construct breadcrumb items from API response or category slug
  const breadcrumbs = useMemo(() => {
    const list = [{ id: "home", title: "فروشگاه اینترنتی زومیو" }];

    // If products are loaded and contain breadcrumbs info, use the API chain
    if (products.length > 0 && products[0].breadcrumbs) {
      products[0].breadcrumbs.forEach((b, idx) => {
        list.push({
          id: b.id || `cat-${idx}`,
          title: b.title,
          slug: b.slug,
        });
      });
    } else if (slug) {
      // Fallback if products array is empty but slug exists
      list.push({
        id: "current-cat",
        title: slug.replace(/-/g, " "),
        slug: slug,
      });
    }

    return list;
  }, [products, slug]);

  const handleFilterChange = (newFilters) => {
    setActiveFilters(newFilters);
    setMobileFiltersOpen(false);
  };

  const handleSortChange = (newFilters) => {
    setActiveFilters((prev) => ({ ...prev, ...newFilters }));
  };

  if (error === 404) {
    return <NotFoundPage />;
  }

  if (notFound) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "70vh",
          gap: 2,
          direction: "rtl",
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 64, color: "error.main" }} />
        <Typography variant="h5" fontWeight={700}>
          دسته‌بندی مورد نظر یافت نشد
        </Typography>
        <Typography variant="body2" color="text.secondary">
          آدرس وارد شده معتبر نیست یا این دسته‌بندی حذف شده است.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        direction: "rtl",
        p: { xs: 1.5, md: 4 },
        bgcolor: "#f5f6f8",
        minHeight: "100vh",
      }}
    >
      <BreadcrumbsBar breadcrumbs={breadcrumbs} />
      <Typography
        variant="h5"
        fontWeight="800"
        mb={3}
        sx={{ display: { xs: "none", md: "block" } }}
      >
        انواع {slug?.replace(/-/g, " ")}
      </Typography>

      <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
        {/* DESKTOP RIGHT SIDEBAR CONTAINER */}
        {showDesktopFilters && (
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              flexDirection: "column",
              width: 280,
              height: "calc(100vh - 48px)",
              maxHeight: "calc(100vh - 48px)",
              flexShrink: 0,
              position: "sticky",
              top: 24,
              alignSelf: "flex-start",
              overflow: "hidden",
              bgcolor: "#fff",
              borderRadius: 3,
              border: "1px solid",
              borderColor: "grey.200",
              p: 2,
              boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
                flexShrink: 0,
              }}
            >
              <Typography variant="h6" fontWeight="700">
                فیلترها
              </Typography>
              <IconButton
                size="small"
                onClick={() => setShowDesktopFilters(false)}
                sx={{ bgcolor: "grey.100" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 1.5, flexShrink: 0 }} />

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <FilterSideBar
                activeFilters={activeFilters}
                onApplyFilters={handleFilterChange}
                onClose={() => setShowDesktopFilters(false)}
                categorySlug={slug}
                meta={meta}
              />
            </Box>
          </Box>
        )}

        {/* LEFT COLUMN (Toolbar + Product Grid) */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <ProductSortbar
            activeFilters={activeFilters}
            onFilterChange={handleSortChange}
            onOpenMobileFilters={() => setMobileFiltersOpen(true)}
            showDesktopFilters={showDesktopFilters}
            onToggleDesktopFilters={() =>
              setShowDesktopFilters((prev) => !prev)
            }
          />

          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="50vh"
            >
              <CircularProgress color="error" />
            </Box>
          ) : products.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="40vh"
            >
              <Typography variant="h6" color="text.secondary">
                محصولی با این مشخصات یافت نشد.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: { xs: 1, md: 2 },
              }}
            >
              {products.map((product) => {
                // Backend Discount shape: { name, percent, end_date } — derive final price.
                const activeDiscount =
                  Array.isArray(product.discounts) && product.discounts.length > 0
                    ? product.discounts[0]
                    : null;
                const rawOriginal = product.min_price || product.price;
                const pct = Number(activeDiscount?.percent || 0);
                const rawPrice =
                  activeDiscount && rawOriginal && pct > 0 && pct < 100
                    ? String(Math.round(Number(rawOriginal) * (1 - pct / 100)))
                    : rawOriginal;

                const hasPrice =
                  rawPrice !== null &&
                  rawPrice !== undefined &&
                  rawPrice !== "";
                const hasDiscount =
                  Boolean(activeDiscount) &&
                  rawOriginal &&
                  Number(rawOriginal) > Number(rawPrice);

                const isAvailable = product.in_stock ?? hasPrice;

                // Backend sends prices in toman — display as-is.
                const productPriceInToman = hasPrice
                  ? Math.floor(Number(rawPrice))
                  : null;
                const originalPriceInToman =
                  hasDiscount && rawOriginal
                    ? Math.floor(Number(rawOriginal))
                    : null;

                return (
                  <Card
                    key={product.id || product.slug}
                    elevation={0}
                    onClick={() => navigate(`/product/${product.slug}`)}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: { xs: 2, md: 3 },
                      position: "relative",
                      bgcolor: "#fff",
                      border: "1px solid",
                      borderColor: "grey.200",
                      cursor: "pointer",
                      transition: "box-shadow 0.2s",
                      WebkitTapHighlightColor: "transparent",
                      WebkitTouchCallout: "none",
                      userSelect: "none",
                      "&:hover": {
                        boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
                      },
                    }}
                  >
                    {/* Unavailable Badge */}
                    {!isAvailable && (
                      <Chip
                        label="اتمام موجودی"
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          zIndex: 4,
                          bgcolor: "rgba(0, 0, 0, 0.7)",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          borderRadius: 1.5,
                          backdropFilter: "blur(4px)",
                        }}
                      />
                    )}
                    {isAvailable && hasDiscount && (
                      <Chip
                        label={pct ? `٪${pct}` : "تخفیف"}
                        size="small"
                        color="error"
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          zIndex: 4,
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          borderRadius: 1.5,
                        }}
                      />
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        justifyContent: "flex-start",
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          opacity: isAvailable ? 1 : 0.55,
                          filter: isAvailable ? "none" : "grayscale(40%)",
                          transition: "0.2s",
                        }}
                      >
                        <ProductImage
                          images={product.images}
                          title={product.title}
                        />
                      </Box>

                      <CardContent
                        sx={{
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                          p: { xs: 1.2, md: 2 },
                        }}
                      >
                        <Typography
                          sx={{
                            height: "3em",
                            lineHeight: 1.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            fontWeight: 600,
                            fontSize: { xs: "0.8rem", md: "0.9rem" },
                            color: isAvailable
                              ? "text.primary"
                              : "text.secondary",
                            mb: 1.5,
                          }}
                        >
                          {product.title || "نامشخص"}
                        </Typography>

                        <Box
                          sx={{
                            mt: "auto",
                            pt: 1.5,
                            borderTop: "1px solid",
                            borderColor: "divider",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                          }}
                        >
                          {hasPrice && isAvailable && originalPriceInToman && (
                            <Typography
                              sx={{
                                textDecoration: "line-through",
                                color: "text.secondary",
                                fontSize: { xs: "0.75rem", md: "0.85rem" },
                                lineHeight: 1.2,
                              }}
                            >
                              {originalPriceInToman.toLocaleString("en-US")}
                            </Typography>
                          )}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                          <Typography
                            color={
                              isAvailable
                                ? hasDiscount
                                  ? "error.main"
                                  : "primary"
                                : "text.secondary"
                            }
                            fontWeight={800}
                            fontSize={{ xs: "0.95rem", md: "1.1rem" }}
                          >
                            {hasPrice && isAvailable
                              ? productPriceInToman.toLocaleString("en-US")
                              : "ناموجود"}
                          </Typography>

                          {hasPrice && isAvailable && (
                            <Box
                              component="span"
                              color="text.secondary"
                              sx={{ fontSize: "0.75rem", fontWeight: 400 }}
                            >
                              تومان
                            </Box>
                          )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Box>
                  </Card>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* MOBILE / TABLET DRAWER */}
      <Drawer
        anchor="right"
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "85%", sm: 320 },
            p: 2,
            direction: "rtl",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" fontWeight="700">
            فیلترها
          </Typography>
          <IconButton
            onClick={() => setMobileFiltersOpen(false)}
            size="small"
            sx={{ bgcolor: "grey.100" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2, flexShrink: 0 }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <FilterSideBar
            activeFilters={activeFilters}
            onApplyFilters={handleFilterChange}
            onClose={() => setMobileFiltersOpen(false)}
            categorySlug={slug}
            meta={meta}
          />
        </Box>
      </Drawer>
    </Box>
  );
}
